import { Experiment } from '@ms/yammer-data/dist/state/types';
import { useSelectedOnBehalfOfUserId } from '@ms/yammer-hooks/dist/analyticsSettings/selectors';
import { useExperimentTreatmentWithoutReporting } from '@ms/yammer-hooks/dist/experiments';
import { useFeatureAccessInsights, useFeatureAccessInsightsStatus } from '@ms/yammer-hooks/dist/featureAccessInsights';

import { UseUIFeatureCheck } from '../types';

export {
  useIsCampaignsEntryPointVisible as useIsInsightsCampaignEnabled,
  useIsLeadersInsightsEntryPointVisible as useIsInsightsLeadersOrgNavigationEnabled,
  useIsMyInsightsEntryPointVisible as useIsInsightsLeadersMyInsightsNavigationEnabled,
  useIsQnAMyInsightsEntryPointVisible as useIsInsightsQnAMyInsightsEnabled,
  useIsQnAOrgEntryPointVisible as useIsInsightsQnAOrgEnabled,
} from '@ms/yammer-hooks/dist/insights';

const useInsightsLeadersPrerequisites: UseUIFeatureCheck<[], boolean | undefined> = () => {
  const vivaEngagePremiumExperiment = useExperimentTreatmentWithoutReporting(Experiment.VivaEngagePremium);
  const featureAccessInsights = useFeatureAccessInsights();
  const featureAccessInsightsStatus = useFeatureAccessInsightsStatus();
  const selectedOnBehalfOfUserId = useSelectedOnBehalfOfUserId();

  if (vivaEngagePremiumExperiment !== true) {
    return false;
  }

  if (
    featureAccessInsightsStatus === 'pending' ||
    featureAccessInsights?.onBehalfOfUserId !== selectedOnBehalfOfUserId
  ) {
    return undefined;
  }

  return true;
};

export const useIsInsightsLeadersMyInsightsPageEnabled: UseUIFeatureCheck<[], boolean | undefined> = () => {
  const insightsLeadersPrerequisites = useInsightsLeadersPrerequisites();
  const featureAccessInsights = useFeatureAccessInsights();

  return insightsLeadersPrerequisites && featureAccessInsights?.canViewActivity === true;
};

export const useIsInsightsLeadersOrgPageEnabled: UseUIFeatureCheck<[], boolean | undefined> = () => {
  const insightsLeadersPrerequisites = useInsightsLeadersPrerequisites();
  const featureAccessInsights = useFeatureAccessInsights();

  return insightsLeadersPrerequisites && featureAccessInsights?.canViewLeadershipActivity === true;
};

export const useIsUserInsightsEnabled: UseUIFeatureCheck = () =>
  useExperimentTreatmentWithoutReporting(Experiment.UserInsights) === true;

export const useIsAnalyticsDiscoveryBubbleEnabled: UseUIFeatureCheck = () =>
  useExperimentTreatmentWithoutReporting(Experiment.AnalyticsDiscoveryBubble) === true;

export const useIsVivaInsightsNetworkEnabled: UseUIFeatureCheck<[], boolean | undefined> = () => {
  const featureAccessInsights = useFeatureAccessInsights();
  const insightsLeadersPrerequisites = useInsightsLeadersPrerequisites();
  const isNetworkAnalyticsEnabled =
    useExperimentTreatmentWithoutReporting(Experiment.VivaInsightsNetwork) === true &&
    featureAccessInsights?.canViewNetworkActivity === true;
  const selectedOnBehalfOfUserId = useSelectedOnBehalfOfUserId();
  const isLeaderSelectedOnBehalfOfViewer = !!selectedOnBehalfOfUserId;

  return insightsLeadersPrerequisites && isNetworkAnalyticsEnabled && isLeaderSelectedOnBehalfOfViewer === false;
};

export const useIsVivaInsightsAudienceThemeCardEnabled: UseUIFeatureCheck = () =>
  useExperimentTreatmentWithoutReporting(Experiment.VivaInsightsAudienceThemeCard) === true;

export const useIsConversationInsightsV2Enabled: UseUIFeatureCheck = () =>
  useExperimentTreatmentWithoutReporting(Experiment.ConversationInsightsV2) === true;

export const useIsCommunityInsightsV2Enabled: UseUIFeatureCheck = () =>
  useExperimentTreatmentWithoutReporting(Experiment.CommunityInsightsV2) === true;
